import $ from 'jquery';
import { resolveSetOptions } from './utils/set-variants.mjs';
import { remainingPromotionTime } from './utils/promotion-countdown.mjs';
import { enhanceOfferChoices } from './utils/offer-choices.mjs';
import { enhanceCrossSellRail } from './utils/product-cross-sell.mjs';
import { createBoosterMotion } from './utils/booster-motion.mjs';
import '../sass/components/promotions.scss';

const config = window.ovaliaPromotions;
if (config) {
    const motion = createBoosterMotion(document);
    const states = new Map();
    const offerViews = new WeakMap();
    let railSection;
    let disposeRail;
    let railPosition = { id: null, index: 0 };
    let adding = false;
    let pendingOffer = null;
    let refreshing = false;
    let serverOffset = 0;
    let expiryRefresh = '';
    let focusedOffer = null;
    const lockedControls = new Map();
    const syncButton = (form) => {
        const view = offerViews.get(form);
        if (!view) return;
        const phase = pendingOffer?.id === form.dataset.boosterOffer ? pendingOffer.phase : '';
        const button = form.querySelector('button[type="submit"]');
        const label = button.querySelector('[data-offer-button-label]');
        if (phase) button.dataset.state = phase;
        else delete button.dataset.state;
        button.setAttribute('aria-busy', String(phase === 'loading'));
        form.setAttribute('aria-busy', String(phase === 'loading'));
        label.textContent = phase === 'loading' ? config.adding : phase === 'success' ? config.addedButton : view.label;
    };
    const syncPurchaseLock = () => {
        if (adding) {
            document.querySelectorAll('[data-booster-offer] button, [data-booster-offer] select, [data-booster-offer] input[type="radio"], #place_order').forEach((control) => {
                if (!lockedControls.has(control)) lockedControls.set(control, control.disabled);
                control.disabled = true;
            });
        } else {
            lockedControls.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
            lockedControls.clear();
        }
        document.querySelectorAll('[data-booster-offer]').forEach(syncButton);
    };

    const initializeOffer = (form, animate = false) => {
        if (!offerViews.has(form)) {
            const photo = form.querySelector('.OvCheckoutBooster__image img');
            offerViews.set(form, {
                controls: enhanceOfferChoices(form, config.assets),
                price: form.querySelector('[data-offer-price]').innerHTML,
                saving: form.querySelector('[data-offer-saving]')?.innerHTML || '',
                image: photo && { src: photo.getAttribute('src'), srcset: photo.getAttribute('srcset') || '' },
                label: form.querySelector('[data-offer-button-label]').textContent,
            });
        }
        const view = offerViews.get(form);
        const id = form.dataset.boosterOffer;
        const choices = JSON.parse(form.querySelector('[data-offer-choices]').textContent);
        const selects = [...form.querySelectorAll('[data-offer-attribute]')];
        const saved = states.get(id) || {};
        const attributes = choices.attributes.map((attribute) => ({ ...attribute, options: attribute.options.map((option) => option.value) }));
        const selected = Object.fromEntries(selects.map((select) => [select.dataset.offerAttribute, select.value || saved[select.dataset.offerAttribute] || '']));
        const result = resolveSetOptions(choices.variants, attributes, selected);
        selects.forEach((select) => {
            [...select.options].forEach((option) => { option.disabled = !!option.value && !result.available[select.dataset.offerAttribute].includes(option.value); });
            select.value = result.selections[select.dataset.offerAttribute];
        });
        states.set(id, result.selections);
        view.controls.sync();
        form.querySelector('[data-offer-price]').innerHTML = result.variant?.offer_html ?? view.price;
        const saving = form.querySelector('[data-offer-saving]');
        if (saving) saving.innerHTML = result.variant?.saving_html ?? view.saving;
        const photo = form.querySelector('.OvCheckoutBooster__image img');
        const preview = choices.variants.find((variant) => Object.entries(result.selections).every(([name, value]) => !value || !variant.attributes[name] || variant.attributes[name] === value));
        const chosenImage = Object.values(result.selections).some(Boolean) && preview?.image?.src ? preview.image : view.image;
        motion.image(photo, chosenImage, animate);
        return result;
    };

    const initializeOffers = () => {
        document.querySelectorAll('[data-booster-offer]').forEach((form) => initializeOffer(form));
        const section = document.querySelector('.OvCheckoutBooster');
        if (section === railSection) return;
        disposeRail?.(); railSection = section;
        motion.observe(section);
        const grid = section?.querySelector('[data-cross-sell-rail]');
        if (!grid) return;
        const disposeControls = enhanceCrossSellRail(section);
        const cards = [...grid.children];
        const remember = () => {
            if (grid.scrollWidth <= grid.clientWidth + 2) return;
            const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : grid.clientWidth;
            const index = Math.min(cards.length - 1, Math.round(grid.scrollLeft / (step || 1)));
            railPosition = { id: cards[index]?.dataset.boosterOffer, index };
        };
        grid.addEventListener('scroll', remember, { passive: true });
        disposeRail = () => { disposeControls?.(); grid.removeEventListener('scroll', remember); };
        const same = cards.findIndex((card) => card.dataset.boosterOffer === railPosition.id);
        const index = same >= 0 ? same : Math.min(railPosition.index, cards.length - 1);
        requestAnimationFrame(() => {
            if (!grid.isConnected || !cards[index]) return;
            grid.scrollLeft = cards[index].offsetLeft - cards[0].offsetLeft;
            grid.dispatchEvent(new Event('scroll'));
        });
    };
    const applyFragments = async (result, animate = false) => {
        if (!result.fragments) throw new Error(config.error);
        const before = animate ? motion.capture() : null;
        Object.entries(result.fragments).forEach(([selector, html]) => $(selector).replaceWith(html));
        const params = window.wc_cart_fragments_params;
        if (params) {
            try {
                const cartFragments = Object.fromEntries(Object.entries(result.fragments).filter(([selector]) => selector !== '[data-header-topbar]'));
                sessionStorage.setItem(params.fragment_name, JSON.stringify(cartFragments));
                sessionStorage.setItem(params.cart_hash_key, result.cart_hash);
                localStorage.setItem(params.cart_hash_key, result.cart_hash);
                if (result.cart_hash) sessionStorage.setItem('wc_cart_created', String(Date.now()));
            } catch (_) { /* Storage is optional; the server cart remains authoritative. */ }
        }
        $(document.body).trigger('wc_fragments_refreshed');
        if (before) await motion.settle(before);
        document.dispatchEvent(new Event('ovalia:cart-updated'));
    };
    const fetchWithTimeout = async (url, data) => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 20000);
        try {
            const response = await fetch(url, { method: 'POST', credentials: 'same-origin', body: data, signal: controller.signal, cache: 'no-store' });
            const serverDate = Date.parse(response.headers.get('Date'));
            if (Number.isFinite(serverDate)) serverOffset = serverDate - Date.now();
            const result = await response.json();
            if (!response.ok || result.success === false) throw new Error(result.data?.message || config.error);
            return result;
        } finally { clearTimeout(timer); }
    };
    const refreshPromotions = async () => {
        if (refreshing || adding || !window.wc_cart_fragments_params) return;
        refreshing = true;
        try {
            const url = window.wc_cart_fragments_params.wc_ajax_url.replace('%%endpoint%%', 'get_refreshed_fragments');
            const data = new FormData();
            if (document.querySelector('[data-header-topbar]')) data.set('ovalia_refresh_topbar', '1');
            const result = await fetchWithTimeout(url, data);
            if (!adding) await applyFragments(result);
        } catch (_) {
            // The visible expired state remains; checkout also validates the deadline server-side.
        } finally { refreshing = false; }
    };
    const tick = () => {
        const expired = [];
        document.querySelectorAll('[data-auto-coupon]').forEach((offer) => {
            const countdown = remainingPromotionTime(offer.dataset.expires, Date.now() + serverOffset);
            offer.querySelector('[data-coupon-countdown]').textContent = countdown.expired ? config.expired : `${countdown.days ? `${countdown.days} ${countdown.days === 1 ? config.day : config.days} ` : ''}${countdown.text}`;
            const label = offer.querySelector('[data-coupon-time-label]');
            if (label) { label.textContent = label.dataset.couponRemainingLabel; label.hidden = countdown.expired; }
            offer.classList.toggle('is-expired', countdown.expired);
            if (countdown.expired && offer.hasAttribute('data-product-coupon')) offer.hidden = true;
            if (countdown.expired && offer.hasAttribute('data-topbar-coupon')) {
                offer.hidden = true;
                const topbar = offer.closest('[data-header-topbar]');
                const announcement = topbar.querySelector('[data-topbar-announcement]');
                announcement.hidden = !announcement.textContent.trim();
                topbar.hidden = announcement.hidden;
            }
            if (countdown.expired) expired.push(offer.dataset.expires);
        });
        const key = [...new Set(expired)].sort().join(',');
        if (!key) expiryRefresh = '';
        if (key && expiryRefresh !== key && !adding && !refreshing) {
            expiryRefresh = key;
            refreshPromotions();
        }
    };
    document.addEventListener('ovalia:coupon-changed', tick);
    document.addEventListener('change', (event) => {
        const form = event.target.closest('[data-booster-offer]');
        if (form) {
            form.querySelector('[data-offer-feedback]').textContent = '';
            // Clearing a choice must not resurrect its previous selection from a fragment.
            states.set(form.dataset.boosterOffer, Object.fromEntries([...form.querySelectorAll('[data-offer-attribute]')].map((select) => [select.dataset.offerAttribute, select.value])));
            initializeOffer(form, true);
        }
    });
    document.addEventListener('submit', async (event) => {
        const form = event.target.closest('[data-booster-offer]');
        if (!form) return;
        event.preventDefault();
        if (adding || document.querySelector('form.checkout.processing')) return;
        const feedback = form.querySelector('[data-offer-feedback]');
        if (!initializeOffer(form).variant) { feedback.textContent = config.choose; offerViews.get(form).controls.focusMissing(); return; }
        const data = new FormData(form);
        adding = true;
        pendingOffer = { id: form.dataset.boosterOffer, phase: 'loading' };
        feedback.textContent = '';
        syncPurchaseLock();
        try {
            const result = await fetchWithTimeout(config.ajaxUrl, data);
            if (!result.fragments) throw new Error(config.error);
            pendingOffer.phase = 'success'; syncPurchaseLock();
            await motion.confirm(document.querySelector(`[data-booster-offer="${pendingOffer.id}"]`));
            states.delete(form.dataset.boosterOffer);
            await applyFragments(result, true);
            const status = document.querySelector('[data-booster-status]');
            if (status) status.textContent = config.added;
            const next = document.querySelector('[data-booster-heading], [data-booster-offer] button, #order_review_heading');
            if (next) { next.setAttribute('tabindex', '-1'); next.focus({ preventScroll: true }); }
        } catch (error) {
            const currentFeedback = document.querySelector(`[data-booster-offer="${form.dataset.boosterOffer}"] [data-offer-feedback]`) || feedback;
            currentFeedback.textContent = ['TypeError', 'SyntaxError', 'AbortError'].includes(error.name) ? config.error : error.message;
        } finally {
            adding = false;
            pendingOffer = null;
            syncPurchaseLock();
            initializeOffers();
        }
    });
    $(document.body).on('update_checkout.ovaliaPromotions', () => {
        const active = document.activeElement;
        focusedOffer = active?.closest('[data-booster-offer]') && active.id ? active.id : null;
    });
    $(document.body).on('updated_checkout.ovaliaPromotions wc_fragments_loaded.ovaliaPromotions wc_fragments_refreshed.ovaliaPromotions', () => {
        initializeOffers(); syncPurchaseLock(); tick();
        if (focusedOffer && document.activeElement === document.body) document.getElementById(focusedOffer)?.focus({ preventScroll: true });
        focusedOffer = null;
    });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) { tick(); refreshPromotions(); } });
    const start = () => {
        const timestamp = Number(document.querySelector('[data-auto-coupon]')?.dataset.serverNow);
        if (timestamp > 0) serverOffset = timestamp * 1000 - Date.now();
        initializeOffers(); tick();
        if (document.querySelector('[data-auto-coupon]')) refreshPromotions();
        setInterval(tick, 1000);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
}
